import React from 'react'
import { Link } from 'react-router-dom'
import { 
  Target, Sparkles, Brain, Compass, ArrowRight, Download, 
  Eye, Edit3, Share2, Clock, CheckCircle, AlertCircle,
  FileText, Lightbulb, Users, TrendingUp, Palette, Megaphone
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useJourneyStore } from '../store/journeyStore'
import { anchorsAPI, pdfAPI } from '../lib/api'
import toast from 'react-hot-toast'

const AnchorsPage = () => {
  const [selectedAnchor, setSelectedAnchor] = React.useState(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false)
  const { anchors, loadJourneyStatus } = useJourneyStore()

  React.useEffect(() => {
    loadJourneyStatus()
  }, [loadJourneyStatus])

  const anchorDefinitions = [
    {
      key: 'truth',
      name: 'Truth Anchor',
      agent: 'Manthan',
      description: 'The fundamental human tensions and category dynamics your brand addresses',
      longDescription: 'Discover the deeper philosophical foundations that transcend functional benefits. This anchor reveals the cultural tensions, consumer contradictions, and category opportunities that your brand can uniquely address.',
      icon: Target,
      color: 'red',
      gradient: 'from-red-500 to-pink-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      sections: [
        { key: 'category_analysis', name: 'Category Analysis', icon: TrendingUp },
        { key: 'cultural_tensions', name: 'Cultural Tensions', icon: Users },
        { key: 'consumer_contradictions', name: 'Consumer Contradictions', icon: AlertCircle },
        { key: 'strategic_synthesis', name: 'Strategic Synthesis', icon: Lightbulb }
      ]
    },
    {
      key: 'expression',
      name: 'Expression Anchor', 
      agent: 'Srishti',
      description: 'Your authentic brand voice, character, and distinctive visual identity',
      longDescription: 'Reveal how your truth naturally manifests in voice, character, and visual semiotics. This anchor defines the authentic expression that emerges from your philosophical foundation.',
      icon: Sparkles,
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200', 
      textColor: 'text-blue-800',
      sections: [
        { key: 'brand_voice', name: 'Brand Voice', icon: Megaphone },
        { key: 'brand_character', name: 'Brand Character', icon: Users },
        { key: 'visual_semiotics', name: 'Visual Semiotics', icon: Palette },
        { key: 'distinctive_assets', name: 'Distinctive Assets', icon: Sparkles }
      ]
    },
    {
      key: 'attention',
      name: 'Attention Anchor',
      agent: 'Akarshan', 
      description: 'Attention strategies that authentically serve your deeper truth',
      longDescription: 'Design attention mechanisms that naturally flow from your truth rather than empty engagement tactics. This anchor ensures your attention strategy serves your philosophical foundation.',
      icon: Brain,
      color: 'amber',
      gradient: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-800',
      sections: [
        { key: 'attention_philosophy', name: 'Attention Philosophy', icon: Brain },
        { key: 'channel_strategy', name: 'Channel Strategy', icon: TrendingUp },
        { key: 'content_themes', name: 'Content Themes', icon: FileText },
        { key: 'engagement_framework', name: 'Engagement Framework', icon: Users }
      ]
    },
    {
      key: 'evolution',
      name: 'Evolution Anchor',
      agent: 'Pravah',
      description: 'Scientific go-to-market execution plans grounded in your truth',
      longDescription: 'Create systematic execution roadmaps that scale your truth-anchored brand. This anchor provides the scientific framework for taking your philosophical foundation to market.',
      icon: Compass,
      color: 'green',
      gradient: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      sections: [
        { key: 'gtm_strategy', name: 'Go-to-Market Strategy', icon: TrendingUp },
        { key: 'scaling_framework', name: 'Scaling Framework', icon: ArrowRight },
        { key: 'measurement_system', name: 'Measurement System', icon: Brain },
        { key: 'evolution_roadmap', name: 'Evolution Roadmap', icon: Compass }
      ]
    }
  ]

  const getAnchorStatus = (anchorData) => {
    if (!anchorData) return 'pending'
    if (anchorData.completed) return 'completed'
    if (anchorData.in_progress) return 'in_progress'
    return 'pending'
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'in_progress':
        return <Clock className="w-5 h-5 text-amber-600" />
      default:
        return <AlertCircle className="w-5 h-5 text-secondary-400" />
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Complete'
      case 'in_progress':
        return 'In Progress' 
      default:
        return 'Pending'
    }
  }

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true)
    try {
      const result = await pdfAPI.generateBrandStrategy()
      toast.success('PDF generation started! Check documents page for download.')
    } catch (error) {
      toast.error('Failed to generate PDF. Please try again.')
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const completedAnchors = anchorDefinitions.filter(anchor => 
    getAnchorStatus(anchors[anchor.key]) === 'completed'
  ).length

  const renderAnchorContent = (anchor, data) => {
    if (!data || !data.content) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
          <p className="text-secondary-600">This anchor hasn't been developed yet.</p>
          <Link to="/journey" className="btn-primary mt-4">
            Continue Journey
          </Link>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {anchor.sections.map((section, index) => {
          const sectionData = data.content[section.key]
          const SectionIcon = section.icon
          
          return (
            <motion.div
              key={section.key}
              className="card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-10 h-10 bg-gradient-to-br ${anchor.gradient} rounded-lg flex items-center justify-center`}>
                  <SectionIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900">{section.name}</h3>
              </div>
              
              {sectionData ? (
                <div className="prose prose-sm max-w-none text-secondary-700">
                  {typeof sectionData === 'string' ? (
                    <p>{sectionData}</p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(sectionData).map(([key, value]) => (
                        <div key={key}>
                          <h4 className="font-medium text-secondary-800 capitalize">
                            {key.replace(/_/g, ' ')}
                          </h4>
                          <p className="text-secondary-600">{value}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-secondary-500 italic">No content available for this section.</p>
              )}
            </motion.div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 mb-2">Strategic Anchors</h1>
              <p className="text-lg text-secondary-600">
                The four foundational anchors that ground your brand in deeper human truths
              </p>
            </div>
            
            {completedAnchors >= 4 && (
              <button
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF}
                className="btn-primary"
              >
                {isGeneratingPDF ? (
                  <>
                    <div className="loading-spinner mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Generate PDF
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>

        {/* Progress Summary */}
        <motion.div 
          className="card mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-secondary-900">Journey Progress</h2>
            <span className="text-sm text-secondary-600">{completedAnchors}/4 Complete</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {anchorDefinitions.map((anchor, index) => {
              const status = getAnchorStatus(anchors[anchor.key])
              const Icon = anchor.icon
              
              return (
                <motion.div
                  key={anchor.key}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedAnchor === anchor.key 
                      ? `${anchor.borderColor} ${anchor.bgColor} shadow-md` 
                      : status === 'completed'
                      ? `${anchor.borderColor} ${anchor.bgColor} hover:shadow-md`
                      : 'border-secondary-200 bg-secondary-50 hover:border-secondary-300'
                  }`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  onClick={() => setSelectedAnchor(selectedAnchor === anchor.key ? null : anchor.key)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      status === 'completed' 
                        ? `bg-gradient-to-br ${anchor.gradient} text-white`
                        : 'bg-secondary-200 text-secondary-600'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {getStatusIcon(status)}
                  </div>
                  
                  <h3 className="font-semibold text-secondary-900 mb-1">{anchor.name}</h3>
                  <p className="text-sm text-secondary-600 mb-2">{anchor.description}</p>
                  
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    status === 'completed' ? `${anchor.bgColor} ${anchor.textColor}` :
                    status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                    'bg-secondary-100 text-secondary-600'
                  }`}>
                    {getStatusText(status)}
                  </span>
                </motion.div>
              )
            })}
          </div>
          
          {completedAnchors < 4 && (
            <div className="mt-6 pt-6 border-t border-secondary-200 text-center">
              <p className="text-secondary-600 mb-4">
                Complete your journey to unlock all strategic anchors and generate professional documents.
              </p>
              <Link to="/journey" className="btn-primary">
                Continue Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          )}
        </motion.div>

        {/* Detailed Anchor View */}
        <AnimatePresence>
          {selectedAnchor && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-8 overflow-hidden"
            >
              {(() => {
                const anchor = anchorDefinitions.find(a => a.key === selectedAnchor)
                const data = anchors[selectedAnchor]
                const status = getAnchorStatus(data)
                const Icon = anchor.icon
                
                return (
                  <div className="card">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center bg-gradient-to-br ${anchor.gradient}`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3">
                            <h2 className="text-2xl font-bold text-secondary-900">{anchor.name}</h2>
                            {getStatusIcon(status)}
                          </div>
                          <p className="text-secondary-600">Developed by {anchor.agent} Agent</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {status === 'completed' && (
                          <>
                            <button className="btn-ghost">
                              <Share2 className="w-4 h-4 mr-2" />
                              Share
                            </button>
                            <button className="btn-ghost">
                              <Download className="w-4 h-4 mr-2" />
                              Export
                            </button>
                          </>
                        )}
                        <button 
                          onClick={() => setSelectedAnchor(null)}
                          className="btn-ghost"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <p className="text-lg text-secondary-700">{anchor.longDescription}</p>
                    </div>
                    
                    {renderAnchorContent(anchor, data)}
                  </div>
                )
              })()}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Actions */}
        {!selectedAnchor && (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="card text-center">
              <Compass className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">Continue Journey</h3>
              <p className="text-secondary-600 mb-4">
                Develop more anchors through philosophical conversations
              </p>
              <Link to="/journey" className="btn-primary w-full">
                Continue
              </Link>
            </div>

            {completedAnchors >= 4 && (
              <>
                <div className="card text-center">
                  <FileText className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">Strategic Consultant</h3>
                  <p className="text-secondary-600 mb-4">
                    Get ongoing guidance based on your completed anchors
                  </p>
                  <Link to="/consultant" className="btn-secondary w-full">
                    Ask Consultant
                  </Link>
                </div>

                <div className="card text-center">
                  <Download className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">Download Documents</h3>
                  <p className="text-secondary-600 mb-4">
                    Generate professional PDFs with your strategic foundation
                  </p>
                  <Link to="/documents" className="btn-secondary w-full">
                    View Documents
                  </Link>
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default AnchorsPage
